// src/components/PartnerReviewSummary.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaCheckCircle, FaCommentDots, FaShieldAlt, FaSpinner, FaStar } from 'react-icons/fa';
import { fetchPublicExperienceReviews, type PublicExperienceReview, type PublicReviewSummary } from '../lib/publicReviews';

type PartnerReviewSummaryProps = {
  businessId?: string | null;
  businessSlug?: string | null;
  region?: string | null;
  title?: string;
  subtitle?: string;
  compact?: boolean;
  showEmpty?: boolean;
  className?: string;
};

const emptySummary: PublicReviewSummary = {
  count: 0,
  average_rating: 0,
  distribution: { '1': 0, '2': 0, '3': 0, '4': 0, '5': 0 },
  latest_at: null,
};

const formatDate = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('fr-CA', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
};

const Stars = ({ rating }: { rating: number }) => {
  const value = Math.round(Math.max(0, Math.min(5, Number(rating || 0))));
  return (
    <div className="flex items-center gap-1" aria-label={`${value} étoiles sur 5`}>
      {Array.from({ length: 5 }).map((_, index) => (
        <FaStar key={index} className={index < value ? 'text-amber-400' : 'text-slate-200'} />
      ))}
    </div>
  );
};

export default function PartnerReviewSummary({
  businessId,
  businessSlug,
  region,
  title,
  subtitle,
  compact = false,
  showEmpty = true,
  className = '',
}: PartnerReviewSummaryProps) {
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState<PublicExperienceReview[]>([]);
  const [summary, setSummary] = useState<PublicReviewSummary>(emptySummary);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      setError(null);

      const payload = await fetchPublicExperienceReviews({
        businessId,
        businessSlug,
        region,
        limit: compact ? 3 : 6,
      });

      if (!mounted) return;
      if (!payload.ok) {
        setError(payload.details || payload.error || 'Avis indisponibles');
        setReviews([]);
        setSummary(emptySummary);
      } else {
        setReviews(payload.reviews);
        setSummary(payload.summary || emptySummary);
      }
      setLoading(false);
    };

    load();
    return () => {
      mounted = false;
    };
  }, [businessId, businessSlug, compact, region]);

  const displayTitle = useMemo(() => {
    if (title) return title;
    if (businessId || businessSlug) return 'Réputation partenaire';
    if (region) return `Avis voyageurs — ${region}`;
    return 'Avis publics Forfeo';
  }, [businessId, businessSlug, region, title]);

  if (!loading && !showEmpty && !reviews.length) return null;

  return (
    <section className={`relative overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-5 md:p-7 shadow-xl ${className}`}>
      <div className="absolute -right-20 -top-20 h-52 w-52 rounded-full bg-emerald-100 blur-3xl" />
      <div className="absolute -left-20 bottom-0 h-52 w-52 rounded-full bg-blue-100 blur-3xl" />

      <div className="relative flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-[10px] font-black uppercase tracking-[0.22em] text-emerald-700">
            <FaShieldAlt /> Avis vérifiés & modérés
          </div>
          <h3 className="text-2xl md:text-3xl font-black tracking-tight text-slate-950">{displayTitle}</h3>
          <p className="mt-2 max-w-2xl text-sm font-semibold leading-relaxed text-slate-500">
            {subtitle || 'Les avis publics sont affichés seulement après validation Forfeo pour préserver la qualité, la confiance et la pertinence des recommandations.'}
          </p>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 min-w-[210px]">
          {loading ? (
            <div className="flex items-center gap-3 text-sm font-black text-slate-500"><FaSpinner className="animate-spin" /> Chargement...</div>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <span className="text-4xl font-black text-slate-950">{summary.average_rating ? summary.average_rating.toFixed(1) : '—'}</span>
                <div>
                  <Stars rating={summary.average_rating || 0} />
                  <p className="mt-1 text-xs font-black uppercase tracking-widest text-slate-400">{summary.count} avis public{summary.count > 1 ? 's' : ''}</p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {error && !loading && (
        <div className="relative mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm font-bold text-amber-800">
          Avis publics temporairement indisponibles. {error}
        </div>
      )}

      {!loading && !error && reviews.length === 0 && (
        <div className="relative mt-5 rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center">
          <FaCommentDots className="mx-auto mb-3 text-3xl text-slate-300" />
          <p className="text-lg font-black text-slate-900">Aucun avis public approuvé pour le moment.</p>
          <p className="mt-2 text-sm font-semibold text-slate-500">Les prochains avis validés par Forfeo apparaîtront ici.</p>
          <Link to="/avis-experience" className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-slate-950 px-5 py-3 text-xs font-black uppercase tracking-widest text-white hover:bg-emerald-700 transition">
            Laisser un avis <FaArrowRight />
          </Link>
        </div>
      )}

      {!loading && reviews.length > 0 && (
        <div className="relative mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {reviews.map((review) => (
            <article key={review.id} className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-1 hover:shadow-xl">
              <div className="mb-3 flex items-center justify-between gap-3">
                <Stars rating={review.rating} />
                {review.featured && (
                  <span className="rounded-full bg-amber-50 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-amber-700">Sélection</span>
                )}
              </div>
              <h4 className="text-lg font-black text-slate-950">{review.review_title || 'Expérience Forfeo vérifiée'}</h4>
              <p className="mt-3 line-clamp-4 text-sm font-semibold leading-relaxed text-slate-600">{review.comment}</p>
              <div className="mt-5 flex items-center justify-between border-t border-slate-100 pt-4 text-xs font-bold text-slate-400">
                <span className="inline-flex items-center gap-2 text-slate-600"><FaCheckCircle className="text-emerald-500" /> {review.customer_display_name}</span>
                <span>{formatDate(review.created_at)}</span>
              </div>
              {(review.business_name || review.business_region) && (
                <p className="mt-2 text-xs font-black uppercase tracking-widest text-blue-500">
                  {[review.business_name, review.business_region].filter(Boolean).join(' · ')}
                </p>
              )}
              {review.partner_response && (
                <div className="mt-4 rounded-2xl bg-blue-50 p-3 text-xs font-semibold leading-relaxed text-blue-900">
                  <span className="font-black uppercase tracking-widest">Réponse partenaire</span>
                  <p className="mt-1">{review.partner_response}</p>
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      <div className="relative mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
        <Link to="/avis-publics" className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-xs font-black uppercase tracking-widest text-slate-800 hover:bg-slate-50 transition">
          Voir tous les avis <FaArrowRight />
        </Link>
        <Link to="/avis-experience" className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-5 py-3 text-xs font-black uppercase tracking-widest text-white hover:bg-emerald-700 transition">
          Laisser un avis <FaCommentDots />
        </Link>
      </div>
    </section>
  );
}
